/**
 * Lo que se exporta, dicho una vez para el PDF y para el Excel (29/10).
 *
 * ⚠ **Un documento son SECCIONES, y cada sección es una tabla.** Una pantalla con tres
 * tablas —Reportes: cuentas, flujo y composición— sale en un PDF de tres apartados y en
 * un libro de tres hojas. El PDF lo compone el navegador (`pdf.ts`) y el Excel el
 * servidor (`POST /exportar/excel`, openpyxl), los dos desde ESTE objeto: con dos
 * modelos, el primer ajuste de una columna haría que el PDF y el Excel dijeran cosas
 * distintas de la misma tabla.
 *
 * ⚠ **Las cifras viajan como NÚMEROS y las fechas como ISO.** Un «1.234,56» escrito no
 * suma en Excel; el contador tendría que convertir columna por columna. El formato lo
 * pone quien pinta: el PDF con `monto()`, el Excel con el formato de celda.
 */

export type TipoColumna = "texto" | "monto" | "numero" | "fecha"

export type Columna = {
  titulo: string
  tipo: TipoColumna
}

/** Una celda: texto, número (monto o cantidad), fecha ISO o vacía. */
export type Celda = string | number | null

export type FilaExportable = {
  celdas: Celda[]
  /** Un rótulo que agrupa las filas de debajo («Banesco», «Octubre»): va en negrita. */
  grupo?: boolean
  /** Un resultado dentro de la tabla («= Disponibilidad total»): va en negrita con su raya. */
  total?: boolean
}

export type Seccion = {
  titulo: string
  columnas: Columna[]
  filas: FilaExportable[]
  /** Los totales, si la tabla los trae. Van al pie, en negrita. */
  pie?: Celda[][]
  /**
   * Un cuadro de cifras que ya se explican solas, no un listado: no gana total ni dice cuántos
   * «registros» tiene. Sumadas, sus líneas darían un número falso con aspecto de verdadero.
   */
  cuadro?: boolean
}

/**
 * Una tarjeta de indicador (29/10): «Por pagar · 12 · USD 3.400,00 · BS 1.200,00».
 *
 * ⚠ **Los indicadores van ARRIBA, antes de las tablas**: son lo primero que se mira en
 * la pantalla y lo primero que pidió el usuario ver en el archivo. En el PDF salen como
 * tarjetas; en el Excel, en una hoja «Resumen» con las cifras como números.
 */
export type Indicador = {
  titulo: string
  detalle?: string | null
  /** La cifra grande, como se lee: «12», «5 de 7», «87 %». */
  cantidad?: string | null
  /** Por moneda, como números. */
  montos?: { moneda: string; valor: number }[]
}

export type DocumentoExportable = {
  titulo: string
  /** Período, filtros y búsqueda: lo que dice QUÉ se está viendo. */
  subtitulo?: string
  indicadores?: Indicador[]
  secciones: Seccion[]
}

/** Un nombre de archivo que no rompe en Windows: sin `\/:*?"<>|`. */
export function nombreDeArchivo(titulo: string, extension: "pdf" | "xlsx", hoy = new Date()) {
  const dia = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-${String(hoy.getDate()).padStart(2, "0")}`
  const limpio = titulo
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, " ")
    .trim()
  return `${limpio || "Exportación"} ${dia}.${extension}`
}
